import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import ExpiryTrendChart from "../charts/ExpiryTrendChart";
import { Loader2, ArrowUpRight, Clock, Package, Archive, Filter, RefreshCw, AlertTriangle, Calendar, Trash2, Upload } from "lucide-react";
import { DashboardStats, ExpiryTrend, CategoryDistribution } from "@/utils/dashboardUtils";
import CategoryDistributionChart from "../charts/CategoryDistributionChart";
import { format, parseISO, addDays } from "date-fns";

interface InventoryTabProps {
  stats: DashboardStats;
  expiryTrend: ExpiryTrend[];
  percentages: {
    expiringSoonPercent: number;
    expiredPercent: number;
    healthyPercent: number;
  };
  isLoading: boolean;
  categoryDistribution: CategoryDistribution[];
  items: any[];
}

export default function InventoryTab({ 
  stats, 
  expiryTrend, 
  percentages, 
  isLoading,
  categoryDistribution
}: InventoryTabProps) {
  const [refreshing, setRefreshing] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const navigate = useNavigate();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-expitrack-primary" />
        <span className="ml-3 text-gray-600">Loading inventory data...</span>
      </div>
    );
  }

  const healthSummaryCards = [
    {
      title: "Expired",
      count: stats.recentlyExpired,
      percentage: percentages.expiredPercent,
      background: "bg-gradient-to-r from-red-50 to-red-100",
      textColor: "text-red-500",
      progressBg: "bg-red-100",
      progressFg: "bg-red-500",
      icon: <Clock className="h-5 w-5 text-red-500" />
    },
    {
      title: "Expiring Soon",
      count: stats.expiringSoon,
      percentage: percentages.expiringSoonPercent,
      background: "bg-gradient-to-r from-amber-50 to-amber-100",
      textColor: "text-amber-500",
      progressBg: "bg-amber-100",
      progressFg: "bg-amber-500",
      icon: <Clock className="h-5 w-5 text-amber-500" />
    },
    {
      title: "Healthy",
      count: Math.max(0, stats.totalInventory - stats.recentlyExpired - stats.expiringSoon),
      percentage: percentages.healthyPercent,
      background: "bg-gradient-to-r from-green-50 to-green-100",
      textColor: "text-green-500",
      progressBg: "bg-green-100",
      progressFg: "bg-green-500",
      icon: <Package className="h-5 w-5 text-green-500" />
    }
  ];

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      toast.success("Inventory data refreshed", {
        position: "bottom-right",
        dismissible: true,
      });
    } catch (error: any) {
      console.error("Error refreshing inventory:", error.message);
      toast.error("Failed to refresh inventory data", {
        position: "bottom-right",
        dismissible: true,
      });
    } finally {
      setRefreshing(false);
    }
  };
  
  const generateUpcomingTasks = () => {
    const today = new Date();
    let tasks = [];
    
    if (stats.recentlyExpired > 0) {
      tasks.push({
        title: "Clear expired items",
        description: `${stats.recentlyExpired} items need to be removed from inventory`,
        icon: <Trash2 className="h-5 w-5 text-red-500" />,
        priority: "high",
        dueDate: format(today, "MMM dd, yyyy")
      });
    }
    
    if (stats.expiringSoon > 0) {
      tasks.push({
        title: "Check expiring items",
        description: `${stats.expiringSoon} items are expiring soon and need attention`,
        icon: <AlertTriangle className="h-5 w-5 text-amber-500" />,
        priority: "medium",
        dueDate: format(addDays(today, 2), "MMM dd, yyyy")
      });
    }
    
    tasks.push({
      title: "Monthly inventory audit",
      description: "Verify physical inventory matches digital records",
      icon: <Package className="h-5 w-5 text-blue-500" />,
      priority: "normal",
      dueDate: format(new Date(today.getFullYear(), today.getMonth() + 1, 1), "MMM dd, yyyy")
    });
    
    if (categoryDistribution.length > 0) {
      tasks.push({
        title: "Optimize category distribution",
        description: "Review and balance inventory across categories",
        icon: <Filter className="h-5 w-5 text-purple-500" />,
        priority: "low",
        dueDate: format(addDays(today, 14), "MMM dd, yyyy")
      });
    }
    
    return tasks;
  };
  
  const handleExport = async () => {
    setExportLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast.success("Inventory report exported successfully", {
        position: "bottom-right",
        dismissible: true,
      });
    } catch (error) {
      toast.error("Failed to export inventory report", {
        position: "bottom-right",
        dismissible: true,
      });
    } finally {
      setExportLoading(false);
    }
  };
  
  const navigateToInventory = () => {
    navigate('/inventory');
  };

  const tasks = generateUpcomingTasks();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-2">
        <h2 className="text-xl font-semibold">Inventory Overview</h2>
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh} 
            disabled={refreshing}
            className="flex items-center"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            Refresh Data
          </Button>
          <Button 
            variant="default" 
            size="sm" 
            onClick={navigateToInventory}
            className="flex items-center"
          >
            <ArrowUpRight className="h-4 w-4 mr-1" />
            Full Inventory
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {healthSummaryCards.map((card, index) => (
          <Card key={index} className="shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className={`py-4 ${card.background} rounded-t-lg`}>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">{card.title}</CardTitle>
                {card.icon}
              </div>
            </CardHeader>
            <CardContent className="pt-4 pb-2">
              <div className="flex items-center justify-between">
                <span className={`text-3xl font-bold ${card.textColor}`}>{card.count}</span>
                <Badge className={card.percentage > 0 ? "bg-gray-100 text-gray-700" : "bg-gray-50 text-gray-400"}>
                  {card.percentage}%
                </Badge>
              </div>
              <div className="mt-3">
                <Progress 
                  value={card.percentage} 
                  className={`h-2 ${card.progressBg}`}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Category Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <CategoryDistributionChart categoryDistribution={categoryDistribution} />
          </CardContent>
        </Card>
        
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Expiry Forecast</CardTitle>
          </CardHeader>
          <CardContent>
            <ExpiryTrendChart expiryTrend={expiryTrend} />
          </CardContent>
        </Card>
      </div>
      
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Upcoming Inventory Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tasks.map((task, index) => (
              <div 
                key={index} 
                className="p-4 border rounded-md flex items-start space-x-4 hover:bg-gray-50 transition-colors"
              >
                <div className="bg-gray-100 p-2 rounded-full">
                  {task.icon}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <h3 className="font-medium">{task.title}</h3>
                    <Badge variant={task.priority === 'high' ? 'destructive' : task.priority === 'medium' ? 'default' : 'outline'}>
                      {task.priority}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                  <div className="flex items-center mt-2 text-sm text-gray-500">
                    <Calendar className="h-3.5 w-3.5 mr-1" />
                    Due: {task.dueDate}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
        <CardFooter className="bg-gray-50 rounded-b-lg">
          <div className="w-full flex justify-between items-center">
            <span className="text-sm text-gray-500">Export inventory report</span>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exportLoading}>
              {exportLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Export CSV
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
